// this file contains actions which are always created for a service
import getProp from 'lodash.get'
import isEqual from 'lodash.isequal'
import { invokeHooks } from '../utils'

export default ({ config }) => ({
  // hook action executed after auth => true
  async autoLoad (context) {
    if (config.hooks.autoLoad && typeof config.hooks.autoLoad === 'function') {
      return config.hooks.autoLoad(context)
    }
  },
  // calls the first defined GET request or the given name
  // also ensures that items are loaded from beginning
  async refreshItems ({ commit, dispatch, state }, actionName = undefined) {
    let getActionName = actionName || Object.keys(config.actions.get)[0]
    if (getActionName) {
      // the refresh should happen even if the parameters are same as previous
      commit('setLatestRequest', {
        name: getActionName,
        masterActiveKey: state.masterActiveKey,
        request: null
      })
      return dispatch(getActionName)
    }
  },
  async removeItem ({ state, commit, dispatch }, item) {
    let key = getProp(item, config.items.keyField, undefined)
    let ix = state.items.findIndex(item => isEqual(getProp(item, config.items.keyField, null), key))
    if (ix > -1) {
      commit('clearItemsByIndex', [ix])
    }
    let mx = state.markedItemKeys.indexOf(key)
    if (mx > -1) {
      commit('clearMarkedByIndex', [mx])
    }
    if (getProp(state.activeItem, config.items.keyField, undefined) === key) {
      return dispatch('selectActiveItem', null)
    } else {
      return true
    }
  },
  async removeItemsByKey ({ state, commit, dispatch }, itemKeys) {
    let deselectActive = false
    let clearItems = []
    let clearMarked = []
    itemKeys.forEach(key => {
      let ix = state.items.findIndex(item => isEqual(getProp(item, config.items.keyField, null), key))
      if (ix > -1) {
        clearItems.push(ix)
      }
      let mx = state.markedItemKeys.indexOf(key)
      if (mx > -1) {
        clearMarked.push(mx)
      }
      if (getProp(state.activeItem, config.items.keyField, undefined) === key) {
        deselectActive = true
      }
    })
    commit('clearItemsByIndex', clearItems)
    commit('clearMarkedByIndex', clearMarked)
    if (deselectActive) {
      return dispatch('selectActiveItem', null)
    } else {
      return true
    }
  },
  replaceItems ({ commit }, items) {
    // TODO: should we try to keep as many selected items as possible instead of clearing selection?
    commit('clearMarkedItems')
    commit('setItems', items)
  },
  // saves the currently edited item and removes it from the editingItems
  async saveItem ({ state, commit, dispatch }, actionName = undefined) {
    const createActionName = actionName || Object.keys(config.actions.create)[0]
    const updateActionName = actionName || Object.keys(config.actions.update)[0]
    const ced = state.currentlyEdited
    if (ced) {
      let ok = false
      if (ced.existing) {
        await dispatch(updateActionName, ced)
        ok = true
      } else {
        await dispatch(createActionName, ced)
        ok = true
      }
      if (ok) {
        commit('clearCurrentlyEdited')
      }
    }
  },
  // selects the item which is active
  // if doing multiple selects quickly, only the last one needs to be effective
  // notify details-services that master key has changed
  // call some actions if needed
  // return: true if the promise was the last one executed; false in case there has been another request after the current one
  // IMPORTANT: this action doesn't guarantee that this promise is the last one!
  // TODO: think of a better pattern here?
  async selectActiveItem (context, item) {
    let { commit, dispatch, state } = context
    // define all the logic as a promise
    let p = (async () => {
      // let hadItem = !!item
      // let oldKey = getProp(item, config.items.keyField, null)
      commit('setActiveItem', item)
      let masterKeyFieldForUpdate = config.items.keyFieldAsMaster ? config.items.keyFieldAsMaster : config.items.keyField
      let newMasterKey = getProp(item, masterKeyFieldForUpdate, null)
      // if (hadItem && isEqual(oldKey, newKey)) {
      //   context.dispatch('info', `${startCase(name)}: Skipping because active key hasn't changed`)
      //   return true
      // }
      let promises = []
      await invokeHooks('standard', 'selectActiveItem', undefined, { config, context, params: item })
      config.detailsModules.forEach(async details => {
        // all services implement the updateMasterActiveKey
        let pm = dispatch(`${details.name}/updateMasterActiveKey`, newMasterKey, { root: true })
        promises.push(pm)
        await pm
        // but some service can choose to do some more logic
        if (details.onMasterKeyUpdate) {
          details.onMasterKeyUpdate.forEach(actionInfo => {
            // actions dispatched in parallel
            // TODO: need sequentail?
            let p = dispatch(`${details.name}/${actionInfo.name}`, undefined, { root: true })
            promises.push(p)
            // TODO: implement details cache here
          })
        }
      })
      await Promise.all(promises)
    })()
    commit('setLastPromise', {
      name: 'selectActiveItem',
      promise: p
    })
    // wait until the promises finishes
    await p
    // now check if we are still processing the latest promise
    if (state.lastPromises.selectActiveItem === p) {
      return true
    } else {
      return false
    }
  },
  async selectActiveItemByKey ({ dispatch, state }, key) {
    let item = state.items.find(item => isEqual(getProp(item, config.items.keyField, null), key))
    if (item) {
      return dispatch('selectActiveItem', item)
    } else {
      return dispatch('selectActiveItem', null)
    }
  },
  // whenever we change a filter, we call this action
  updateFilter ({ commit }, filter) {
    commit('setFilter', filter)
    // TODO: update of filter should trigger data reload
  },
  // updateMasterActiveKey is used for details-service to update its internal state
  // created as action if we need to trigger something else, also to avoid calling mutations from outside
  updateMasterActiveKey ({ state, commit }, masterActiveKey) {
    if (!state.masterActiveKey || !isEqual(state.masterActiveKey, masterActiveKey)) {
      commit('clearMarkedItems')
      commit('clearItems')
      commit('setMasterActiveKey', masterActiveKey)
      commit('resetPagination')
      // also clear any remaining requests not to rely on them for checks with previous req
      commit('clearLatestRequests')
    }
  },
  // whenever we change sorting params, we call this action
  async updateSort ({ state, commit, dispatch }, sort) {
    const isSameAsPrevious = isEqual(state.sort, sort)
    if (!isSameAsPrevious) {
      commit('setSort', sort)
      commit('resetPagination')
      // return dispatch('refreshItems')
    }
    // we let component decide if it wants to refresh the items
    return !isSameAsPrevious
  },
  // ---------------------
  // logging stuff
  error ({ commit }, err) {
    commit('addError', err)
    config.hooks.error(err)
  },
  info ({ commit }, message) {
    let info = {
      message: message
    }
    commit('addInfo', info)
    config.hooks.info(info)
  },
  warning ({ commit }, message) {
    let warn = {
      message: message
    }
    commit('addWarning', warn)
    config.hooks.warning(warn)
  }
})
